import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTeam } from '@/shared/context/TeamContext'
import { programmaticApi } from '../api/programmatic.api'
import type { ReporteProgrammaticCreateInput, ReporteProgrammaticUpdateInput } from '../types'

function toSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export function useProgrammaticNav() {
  const { selectedArea } = useTeam()
  const { data, isLoading } = useQuery({
    queryKey: ['reportes-programmatic', selectedArea?.id, { page: 1, page_size: 500 }],
    queryFn: () => programmaticApi.getReportes({ page_size: 500 }),
  })

  const grupos = useMemo(() => {
    if (!data?.results) return []
    const seen = new Map<number, string>()
    for (const r of data.results) {
      if (!seen.has(r.grupo_id)) seen.set(r.grupo_id, r.grupo_nombre)
    }
    return Array.from(seen, ([id, name]) => ({ id, name, slug: toSlug(name) }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [data])

  return { grupos, isLoading }
}

export function useReportesProgrammatic(params?: {
  page?: number
  page_size?: number
  search?: string
  grupo_id?: number
  activo?: boolean
}) {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['reportes-programmatic', selectedArea?.id, params],
    queryFn: () => programmaticApi.getReportes(params),
  })
}

export function useCreateReporteProgrammatic() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ReporteProgrammaticCreateInput) =>
      programmaticApi.createReporte(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-programmatic'] })
    },
  })
}

export function useUpdateReporteProgrammatic() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ReporteProgrammaticUpdateInput }) =>
      programmaticApi.updateReporte(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-programmatic'] })
    },
  })
}

export function useDeleteReporteProgrammatic() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => programmaticApi.deleteReporte(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-programmatic'] })
    },
  })
}

export function useToggleActivoProgrammatic() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => programmaticApi.toggleActivo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-programmatic'] })
    },
  })
}

export function useGruposDisponiblesProgrammatic() {
  return useQuery({
    queryKey: ['grupos-disponibles-programmatic'],
    queryFn: () => programmaticApi.getGruposDisponibles(),
  })
}
